import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, CheckCircle, XCircle, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { wacheSummaryItems } from "../lib/wacheSummary";

const summaryToneClasses = {
  neutral: "border-slate-200 bg-slate-50 text-slate-900",
  positive: "border-emerald-200 bg-emerald-50 text-emerald-950",
  warning: "border-amber-200 bg-amber-50 text-amber-950",
  danger: "border-rose-200 bg-rose-50 text-rose-950",
};

const WacheSummary = ({ summary }) => (
  <div className="grid grid-cols-2 gap-2" aria-label="Kennzahlen der Wache">
    {wacheSummaryItems(summary).map((item) => (
      <div
        key={item.label}
        className={`rounded-xl border px-3 py-3 ${summaryToneClasses[item.tone]}`}
      >
        <div className="text-2xl font-semibold leading-none tabular-nums">{item.value}</div>
        <div className="mt-1 text-xs font-medium leading-tight opacity-75">{item.label}</div>
      </div>
    ))}
  </div>
);

const Wachen = () => {
  const [wachen, setWachen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [wacheToDelete, setWacheToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    subdomain: "",
    wache_nummer: "",
  });

  // Helper to ensure we always have an array
  const toArray = (data) => Array.isArray(data) ? data : [];

  useEffect(() => {
    loadWachen();
  }, []);

  const loadWachen = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/wachen`);
      setWachen(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Wachen");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/wachen`, formData);
      toast.success("Wache erfolgreich erstellt");
      setShowCreateDialog(false);
      setFormData({ name: "", subdomain: "", wache_nummer: "" });
      loadWachen();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen");
    }
  };

  const handleToggleStatus = async (wacheId, currentStatus) => {
    try {
      const newStatus = currentStatus === "active" ? "inactive" : "active";
      await axios.patch(`${API}/wachen/${wacheId}`, { status: newStatus });
      toast.success("Status aktualisiert");
      loadWachen();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleDeleteClick = (wache) => {
    setWacheToDelete(wache);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!wacheToDelete) return;
    
    try {
      await axios.delete(`${API}/wachen/${wacheToDelete.id}`);
      toast.success("Wache erfolgreich gelöscht");
      setShowDeleteDialog(false);
      setWacheToDelete(null);
      loadWachen();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-600">Lädt...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="wachen-container">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Wachen</h1>
          <p className="text-gray-600">Verwaltung aller Feuerwachen</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="create-wache-button">
              <Plus className="w-4 h-4 mr-2" />
              Neue Wache
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-gray-300">
            <DialogHeader>
              <DialogTitle className="text-gray-900">Neue Wache erstellen</DialogTitle>
              <DialogDescription className="text-gray-600">
                Fügen Sie eine neue Feuerwache zum System hinzu
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate}>
              <div className="space-y-4 my-4">
                <div>
                  <Label htmlFor="name" className="text-gray-700">Name der Wache</Label>
                  <Input
                    id="name"
                    data-testid="wache-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900"
                    placeholder="z.B. Feuerwache 4200"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="wache_nummer" className="text-gray-700">Wachen-Nummer</Label>
                  <Input
                    id="wache_nummer"
                    data-testid="wache-nummer-input"
                    value={formData.wache_nummer}
                    onChange={(e) => setFormData({ ...formData, wache_nummer: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900"
                    placeholder="z.B. 4200"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="subdomain" className="text-gray-700">Subdomain</Label>
                  <Input
                    id="subdomain"
                    data-testid="wache-subdomain-input"
                    value={formData.subdomain}
                    onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900"
                    placeholder="wache4200.ihre-domain.de"
                    required
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)} className="border-gray-300 text-gray-700">
                  Abbrechen
                </Button>
                <Button type="submit" className="btn-primary" data-testid="wache-submit-button">
                  Erstellen
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Wachen List */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {wachen.length === 0 ? (
          <Card className="card-white">
            <CardContent className="py-12 text-center text-gray-600">
              Noch keine Wachen vorhanden. Erstellen Sie die erste Wache.
            </CardContent>
          </Card>
        ) : (
          toArray(wachen).map((wache) => (
            <Card key={wache.id} className="card-white card-hover flex h-full flex-col" data-testid={`wache-card-${wache.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-gray-900 mb-1 text-lg">{wache.name}</CardTitle>
                    <p className="text-sm text-gray-600 mb-1 truncate">{wache.subdomain}</p>
                    <p className="text-xs text-gray-500">
                      <span className="font-semibold">Wach-ID:</span> <code className="bg-gray-100 px-2 py-0.5 rounded">{wache.id}</code>
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {wache.status === "active" ? (
                      <span className="status-badge status-paid">
                        <CheckCircle className="w-4 h-4" />
                        Aktiv
                      </span>
                    ) : (
                      <span className="status-badge status-overdue">
                        <XCircle className="w-4 h-4" />
                        Inaktiv
                      </span>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col space-y-4 pt-0">
                <WacheSummary summary={wache.summary} />

                {/* Actions */}
                <div className="mt-auto flex flex-wrap gap-2 pt-1">
                  <Button asChild className="bg-slate-900 text-white hover:bg-slate-800">
                    <Link to={`/wachen/${wache.id}`}>Details &amp; Verträge</Link>
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleToggleStatus(wache.id, wache.status)}
                    className="border-gray-300 text-gray-700 hover:bg-gray-100"
                    data-testid={`toggle-status-${wache.id}`}
                  >
                    {wache.status === "active" ? "Deaktivieren" : "Aktivieren"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleDeleteClick(wache)}
                    className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                    data-testid={`delete-wache-${wache.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Löschen
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Wache löschen?</DialogTitle>
            <DialogDescription className="text-gray-600">
              Sind Sie sicher, dass Sie diese Wache löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          {wacheToDelete && (
            <div className="py-4">
              <div className="p-4 rounded-lg bg-gray-50 border border-gray-200 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Name:</span>
                  <span className="font-semibold text-gray-900">{wacheToDelete.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Subdomain:</span>
                  <span className="text-gray-900">{wacheToDelete.subdomain}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Wach-ID:</span>
                  <span className="text-gray-900 font-mono text-sm">{wacheToDelete.id}</span>
                </div>
              </div>
              <p className="mt-3 text-sm text-amber-600 bg-amber-50 p-3 rounded border border-amber-200">
                <strong>Hinweis:</strong> Die Wache kann nur gelöscht werden, wenn keine aktiven Abteilungen mehr zugeordnet sind.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setWacheToDelete(null);
              }}
              className="border-gray-300 text-gray-700"
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-wache"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Ja, löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Wachen;
